import { useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { ArrowRight, ChevronLeft, ChevronRight, Check } from 'lucide-react'
import SEO from '../components/seo/SEO'
import { useLocalePath } from '../hooks/useLocalePath'
import { useInView } from '../lib/useInView'
import HeroScene from '../components/three/HeroScene'
import { SERVICES } from '../data/services'
import styles from './Home.module.css'

function cx(...classes: (string | false | undefined)[]) {
  return classes.filter(Boolean).join(' ')
}

/* ------------------------------------------------------------------ */
/*  Animated Visual: Feasibility & Project Design                      */
/* ------------------------------------------------------------------ */
function FeasibilityVisual({ visible }: { visible: boolean }) {
  const items = [
    { name: 'REDD+', sub: 'Avoided deforestation' },
    { name: 'Blue Carbon', sub: 'Mangrove restoration' },
    { name: 'Biochar', sub: 'Carbon sequestration' },
    { name: 'A/R', sub: 'Afforestation & Reforestation' },
  ]
  return (
    <div className={styles.visualCard}>
      <div className={styles.visualCardHeader}>Project Feasibility</div>
      {items.map((item, i) => (
        <div
          key={item.name}
          className={cx(
            styles.checklistItem,
            visible && styles.checklistItemVisible,
          )}
          style={{ transitionDelay: visible ? `${0.15 + i * 0.12}s` : '0s' }}
        >
          <span className={styles.checkIcon}>
            <Check size={13} strokeWidth={3} />
          </span>
          <div className={styles.checkContent}>
            <span className={styles.checkText}>{item.name}</span>
            <span className={styles.checkSub}>{item.sub}</span>
          </div>
        </div>
      ))}
      <div className={styles.progressTrack}>
        <div
          className={cx(
            styles.progressFill,
            visible && styles.progressFillActive,
          )}
        />
      </div>
      <div className={styles.progressLabel}>
        <span>Viability Score</span>
        <span className={cx(styles.progressValue, visible && styles.progressValueVisible)}>87%</span>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Animated Visual: Monitoring Dashboard                              */
/* ------------------------------------------------------------------ */
function MonitoringVisual({ visible }: { visible: boolean }) {
  const metrics = [
    { label: 'Carbon Sequestered', value: '12,400 tCO\u2082', pct: 82, variant: 'primary' as const },
    { label: 'Biodiversity Index', value: '0.91 / 1.0', pct: 91, variant: 'success' as const },
    { label: 'Community Impact', value: '340 households', pct: 68, variant: 'accent' as const },
  ]
  return (
    <div className={styles.visualCard}>
      <div className={styles.visualCardHeader}>Monitoring Dashboard</div>
      {metrics.map((m, i) => (
        <div
          key={m.label}
          className={cx(styles.metricItem, visible && styles.metricItemVisible)}
          style={{ transitionDelay: visible ? `${0.2 + i * 0.15}s` : '0s' }}
        >
          <div className={styles.metricInfo}>
            <div className={styles.metricLabel}>{m.label}</div>
            <div className={styles.metricValue}>{m.value}</div>
          </div>
          <div className={styles.metricBar}>
            <div
              className={cx(
                styles.metricBarFill,
                styles[`metricBar${m.variant.charAt(0).toUpperCase() + m.variant.slice(1)}` as keyof typeof styles],
                visible && styles.metricBarFillActive,
              )}
              style={{
                transitionDelay: visible ? `${0.4 + i * 0.15}s` : '0s',
                ['--fill-pct' as string]: `${m.pct}%`,
              }}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Animated Visual: Carbon Credit Trading                             */
/* ------------------------------------------------------------------ */
function TradingVisual({ visible }: { visible: boolean }) {
  const chartPoints = [
    { x: 0, y: 70 },
    { x: 20, y: 55 },
    { x: 40, y: 60 },
    { x: 60, y: 40 },
    { x: 80, y: 35 },
    { x: 100, y: 15 },
  ]
  const pathD = chartPoints
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x * 2.8 + 20} ${p.y + 10}`)
    .join(' ')

  return (
    <div className={styles.visualCard}>
      <div className={styles.visualCardHeader}>Credit Trading</div>
      <div className={cx(styles.tradingStats, visible && styles.tradingStatsVisible)}>
        <div className={styles.tradingStat}>
          <span className={styles.tradingStatLabel}>Price / tCO2</span>
          <span className={styles.tradingStatValue}>$18.40</span>
        </div>
        <div className={styles.tradingStat}>
          <span className={styles.tradingStatLabel}>Volume</span>
          <span className={styles.tradingStatValue}>24,600</span>
        </div>
        <div className={styles.tradingTrend}>
          <span className={styles.tradingTrendUp}>+12.3%</span>
        </div>
      </div>
      <div className={cx(styles.chartWrap, visible && styles.chartWrapVisible)}>
        <svg viewBox="0 0 310 95" className={styles.chartSvg}>
          <defs>
            <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--brand-primary)" stopOpacity="0.2" />
              <stop offset="100%" stopColor="var(--brand-primary)" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d={`${pathD} L 300 85 L 20 85 Z`}
            fill="url(#chartGrad)"
            className={cx(styles.chartArea, visible && styles.chartAreaVisible)}
          />
          <path
            d={pathD}
            fill="none"
            stroke="var(--brand-primary)"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={cx(styles.chartLine, visible && styles.chartLineVisible)}
          />
          {chartPoints.map((p, i) => (
            <circle
              key={i}
              cx={p.x * 2.8 + 20}
              cy={p.y + 10}
              r="3"
              fill="var(--brand-primary)"
              className={cx(styles.chartDot, visible && styles.chartDotVisible)}
              style={{ transitionDelay: visible ? `${0.6 + i * 0.08}s` : '0s' }}
            />
          ))}
        </svg>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Animated Visual: Global South Reach                                */
/* ------------------------------------------------------------------ */
function GlobalSouthVisual({ visible }: { visible: boolean }) {
  const hub = { x: 80, y: 32 }
  const dests = [
    { x: 168, y: 55 },
    { x: 48, y: 78 },
    { x: 262, y: 48 },
  ]
  const arcs = [
    `M80 32 Q128 10 168 55`,
    `M80 32 Q28 42 48 78`,
    `M80 32 Q175 8 262 48`,
  ]
  const partners = [
    { name: 'Nigeria', tag: 'West Africa' },
    { name: 'Republic of Congo', tag: 'Congo Basin' },
    { name: 'DR Congo', tag: 'Congo Basin' },
  ]

  return (
    <div className={styles.visualCard}>
      <div className={styles.visualCardHeader}>On the Ground</div>
      <div className={cx(styles.mapWrap, visible && styles.mapWrapVisible)}>
        <svg viewBox="0 0 310 95" className={styles.mapSvg}>
          <g opacity="0.06" fill="var(--brand-primary)" stroke="none">
            <path d="M28 12 L48 6 L68 8 L80 14 L78 24 L65 30 L50 32 L34 28 L24 20 Z"/>
            <path d="M72 40 L80 36 L86 40 L88 50 L84 62 L78 72 L72 70 L66 60 L65 48 Z"/>
            <path d="M142 10 L155 7 L164 12 L164 20 L156 24 L144 22 L140 16 Z"/>
            <path d="M155 20 L163 18 L172 22 L178 32 L176 46 L172 58 L166 64 L158 60 L152 48 L150 34 Z"/>
            <path d="M218 28 L226 26 L230 32 L228 44 L222 50 L218 44 L216 34 Z"/>
            <path d="M240 24 L250 20 L258 24 L260 34 L254 40 L244 38 L240 30 Z"/>
            <path d="M252 44 L264 42 L272 48 L268 54 L256 52 Z"/>
            <path d="M260 58 L274 56 L282 62 L278 70 L266 72 L258 66 Z"/>
          </g>
          {arcs.map((d, i) => (
            <path key={i} d={d} fill="none"
              stroke="var(--brand-primary)" strokeWidth="1.5" strokeLinecap="round"
              className={cx(styles.arcLine, visible && styles.arcLineVisible)}
              style={{ transitionDelay: visible ? `${0.3 + i * 0.2}s` : '0s' }}
            />
          ))}
          {arcs.map((d, i) => (
            <circle key={`pulse-${i}`} r="2.5" fill="var(--brand-accent)">
              <animate attributeName="opacity" values="0;0.85;0.85;0"
                dur={`${2.5 + i * 0.4}s`} repeatCount="indefinite" begin={`${i * 0.8}s`} />
              <animateMotion dur={`${2.5 + i * 0.4}s`} repeatCount="indefinite"
                begin={`${i * 0.8}s`} path={d} />
            </circle>
          ))}
          <circle cx={hub.x} cy={hub.y} r="14"
            fill="var(--brand-primary)"
            className={cx(styles.hubGlow, visible && styles.hubGlowVisible)}
          />
          <circle cx={hub.x} cy={hub.y} r="6"
            fill="var(--brand-primary)"
            className={cx(styles.hubDot, visible && styles.hubDotVisible)}
          />
          {dests.map((d, i) => (
            <g key={i}>
              <circle cx={d.x} cy={d.y} r="11"
                fill="var(--brand-accent)"
                className={cx(styles.destPulse, visible && styles.destPulseVisible)}
                style={{ transitionDelay: visible ? `${0.6 + i * 0.2}s` : '0s' }}
              />
              <circle cx={d.x} cy={d.y} r="5"
                fill="var(--brand-accent)"
                className={cx(styles.destDot, visible && styles.destDotVisible)}
                style={{ transitionDelay: visible ? `${0.6 + i * 0.2}s` : '0s' }}
              />
            </g>
          ))}
        </svg>
      </div>
      <div className={styles.regionList}>
        {partners.map((p, i) => (
          <div key={p.name}
            className={cx(styles.regionItem, visible && styles.regionItemVisible)}
            style={{ transitionDelay: visible ? `${0.9 + i * 0.1}s` : '0s' }}
          >
            <span className={styles.regionDot} />
            <span className={styles.regionName}>{p.name}</span>
            <span className={styles.regionTag}>{p.tag}</span>
          </div>
        ))}
      </div>
      <div
        className={cx(styles.networkReach, visible && styles.networkReachVisible)}
      >
        West Africa · South America · Southeast Asia
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Main Home Component                                                */
/* ------------------------------------------------------------------ */
export default function Home() {
  const { t } = useTranslation('home')
  const { t: tSeo } = useTranslation('seo')
  const lp = useLocalePath()

  const carouselRef = useRef<HTMLDivElement>(null)
  const scrollCarousel = useCallback((dir: 'left' | 'right') => {
    if (!carouselRef.current) return
    carouselRef.current.scrollBy({ left: dir === 'left' ? -340 : 340, behavior: 'smooth' })
  }, [])

  const heroView = useInView(0.15)
  const statsView = useInView(0.3)
  const feat1View = useInView(0.25)
  const feat2View = useInView(0.25)
  const feat3View = useInView(0.25)
  const feat4View = useInView(0.25)
  const processView = useInView(0.2)
  const servicesView = useInView(0.15)
  const ctaView = useInView(0.3)

  return (
    <>
      <SEO title={tSeo('home.title')} description={tSeo('home.description')} />

      {/* ============================================================ */}
      {/*  HERO                                                        */}
      {/* ============================================================ */}
      <section className={styles.hero} ref={heroView.ref}>
        <div className={styles.heroGrain} />
        <div className={styles.floatShape + ' ' + styles.floatShape1} />
        <div className={styles.floatShape + ' ' + styles.floatShape2} />
        <div className={styles.floatShape + ' ' + styles.floatShape3} />
        <div className={styles.floatShape + ' ' + styles.floatShape4} />
        <div className={styles.floatShape + ' ' + styles.floatShape5} />

        <div className={styles.heroInner}>
          <div className={styles.heroContent}>
            <span
              className={cx(
                styles.heroEyebrow,
                styles.fadeUp,
                heroView.visible && styles.fadeUpVisible,
              )}
            >
              {t('hero.eyebrow')}
            </span>
            <h1
              className={cx(
                styles.heroTitle,
                styles.fadeUp,
                styles.fadeUpD1,
                heroView.visible && styles.fadeUpVisible,
              )}
            >
              {t('hero.title')}
            </h1>
            <p
              className={cx(
                styles.heroLead,
                styles.fadeUp,
                styles.fadeUpD2,
                heroView.visible && styles.fadeUpVisible,
              )}
            >
              {t('hero.lead')}
            </p>
            <div
              className={cx(
                styles.heroActions,
                styles.fadeUp,
                styles.fadeUpD3,
                heroView.visible && styles.fadeUpVisible,
              )}
            >
              <Link to={lp('/contact')} className={styles.btnPrimary}>
                {t('hero.cta1')}
              </Link>
              <Link to={lp('/services')} className={styles.btnOutline}>
                {t('hero.cta2')}
              </Link>
            </div>
            <div
              className={cx(
                styles.trustBadges,
                styles.fadeUp,
                styles.fadeUpD4,
                heroView.visible && styles.fadeUpVisible,
              )}
            >
              <span className={styles.trustPill}>{t('hero.badge1')}</span>
              <span className={styles.trustPill}>{t('hero.badge2')}</span>
              <span className={styles.trustPill}>{t('hero.badge3')}</span>
            </div>
          </div>

          <div
            className={cx(
              styles.heroVisual,
              styles.fadeUp,
              styles.fadeUpD2,
              heroView.visible && styles.fadeUpVisible,
            )}
          >
            <HeroScene />
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  STATS BAR                                                   */}
      {/* ============================================================ */}
      <section className={styles.statsBar} ref={statsView.ref}>
        <div className={styles.statsInner}>
          {[
            { value: '748', label: t('stats.experts') },
            { value: '90%', label: t('stats.referral') },
            { value: '30%', label: t('stats.phds') },
            { value: '80%+', label: t('stats.globalSouth') },
          ].map((stat, i) => (
            <div
              key={stat.label}
              className={cx(
                styles.statItem,
                styles.fadeUp,
                i === 1 && styles.fadeUpD1,
                i === 2 && styles.fadeUpD2,
                i === 3 && styles.fadeUpD3,
                statsView.visible && styles.fadeUpVisible,
              )}
            >
              <span className={styles.statNumber}>{stat.value}</span>
              <span className={styles.statLabel}>{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ============================================================ */}
      {/*  FEATURE 1 — Feasibility (text left, visual right)           */}
      {/* ============================================================ */}
      <section className={styles.featureSection} ref={feat1View.ref}>
        <div className={styles.featureInner}>
          <div className={styles.featureText}>
            <span
              className={cx(
                styles.featureEyebrow,
                styles.fadeUp,
                feat1View.visible && styles.fadeUpVisible,
              )}
            >
              {t('features.feasibility.eyebrow')}
            </span>
            <h2
              className={cx(
                styles.featureTitle,
                styles.fadeUp,
                styles.fadeUpD1,
                feat1View.visible && styles.fadeUpVisible,
              )}
            >
              {t('features.feasibility.title')}
            </h2>
            <p
              className={cx(
                styles.featureDesc,
                styles.fadeUp,
                styles.fadeUpD2,
                feat1View.visible && styles.fadeUpVisible,
              )}
            >
              {t('features.feasibility.description')}
            </p>
            <ul
              className={cx(
                styles.featureBullets,
                styles.fadeUp,
                styles.fadeUpD3,
                feat1View.visible && styles.fadeUpVisible,
              )}
            >
              {[1, 2, 3, 4].map(n => (
                <li key={n}>
                  <span className={styles.bulletCheck}><Check size={12} strokeWidth={3} /></span>
                  {t(`features.feasibility.bullet${n}`)}
                </li>
              ))}
            </ul>
          </div>
          <div
            className={cx(
              styles.featureVisual,
              styles.fadeUp,
              styles.fadeUpD2,
              feat1View.visible && styles.fadeUpVisible,
            )}
          >
            <FeasibilityVisual visible={feat1View.visible} />
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  FEATURE 2 — Monitoring (visual left, text right)            */}
      {/* ============================================================ */}
      <section
        className={cx(styles.featureSection, styles.sectionAlt)}
        ref={feat2View.ref}
      >
        <div className={cx(styles.featureInner, styles.featureReverse)}>
          <div className={styles.featureText}>
            <span
              className={cx(
                styles.featureEyebrow,
                styles.fadeUp,
                feat2View.visible && styles.fadeUpVisible,
              )}
            >
              {t('features.monitoring.eyebrow')}
            </span>
            <h2
              className={cx(
                styles.featureTitle,
                styles.fadeUp,
                styles.fadeUpD1,
                feat2View.visible && styles.fadeUpVisible,
              )}
            >
              {t('features.monitoring.title')}
            </h2>
            <p
              className={cx(
                styles.featureDesc,
                styles.fadeUp,
                styles.fadeUpD2,
                feat2View.visible && styles.fadeUpVisible,
              )}
            >
              {t('features.monitoring.description')}
            </p>
            <ul
              className={cx(
                styles.featureBullets,
                styles.fadeUp,
                styles.fadeUpD3,
                feat2View.visible && styles.fadeUpVisible,
              )}
            >
              {[1, 2, 3, 4].map(n => (
                <li key={n}>
                  <span className={styles.bulletCheck}><Check size={12} strokeWidth={3} /></span>
                  {t(`features.monitoring.bullet${n}`)}
                </li>
              ))}
            </ul>
          </div>
          <div
            className={cx(
              styles.featureVisual,
              styles.fadeUp,
              styles.fadeUpD2,
              feat2View.visible && styles.fadeUpVisible,
            )}
          >
            <MonitoringVisual visible={feat2View.visible} />
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  FEATURE 3 — Trading (text left, visual right)               */}
      {/* ============================================================ */}
      <section className={styles.featureSection} ref={feat3View.ref}>
        <div className={styles.featureInner}>
          <div className={styles.featureText}>
            <span
              className={cx(
                styles.featureEyebrow,
                styles.fadeUp,
                feat3View.visible && styles.fadeUpVisible,
              )}
            >
              {t('features.trading.eyebrow')}
            </span>
            <h2
              className={cx(
                styles.featureTitle,
                styles.fadeUp,
                styles.fadeUpD1,
                feat3View.visible && styles.fadeUpVisible,
              )}
            >
              {t('features.trading.title')}
            </h2>
            <p
              className={cx(
                styles.featureDesc,
                styles.fadeUp,
                styles.fadeUpD2,
                feat3View.visible && styles.fadeUpVisible,
              )}
            >
              {t('features.trading.description')}
            </p>
            <ul
              className={cx(
                styles.featureBullets,
                styles.fadeUp,
                styles.fadeUpD3,
                feat3View.visible && styles.fadeUpVisible,
              )}
            >
              {[1, 2, 3, 4].map(n => (
                <li key={n}>
                  <span className={styles.bulletCheck}><Check size={12} strokeWidth={3} /></span>
                  {t(`features.trading.bullet${n}`)}
                </li>
              ))}
            </ul>
          </div>
          <div
            className={cx(
              styles.featureVisual,
              styles.fadeUp,
              styles.fadeUpD2,
              feat3View.visible && styles.fadeUpVisible,
            )}
          >
            <TradingVisual visible={feat3View.visible} />
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  FEATURE 4 — Global South (visual left, text right)          */}
      {/* ============================================================ */}
      <section
        className={cx(styles.featureSection, styles.sectionAlt)}
        ref={feat4View.ref}
      >
        <div className={cx(styles.featureInner, styles.featureReverse)}>
          <div className={styles.featureText}>
            <span
              className={cx(
                styles.featureEyebrow,
                styles.fadeUp,
                feat4View.visible && styles.fadeUpVisible,
              )}
            >
              {t('features.globalSouth.eyebrow')}
            </span>
            <h2
              className={cx(
                styles.featureTitle,
                styles.fadeUp,
                styles.fadeUpD1,
                feat4View.visible && styles.fadeUpVisible,
              )}
            >
              {t('features.globalSouth.title')}
            </h2>
            <p
              className={cx(
                styles.featureDesc,
                styles.fadeUp,
                styles.fadeUpD2,
                feat4View.visible && styles.fadeUpVisible,
              )}
            >
              {t('features.globalSouth.description')}
            </p>
            <ul
              className={cx(
                styles.featureBullets,
                styles.fadeUp,
                styles.fadeUpD3,
                feat4View.visible && styles.fadeUpVisible,
              )}
            >
              {[1, 2, 3, 4, 5].map(n => (
                <li key={n}>
                  <span className={styles.bulletCheck}><Check size={12} strokeWidth={3} /></span>
                  {t(`features.globalSouth.bullet${n}`)}
                </li>
              ))}
            </ul>
          </div>
          <div
            className={cx(
              styles.featureVisual,
              styles.fadeUp,
              styles.fadeUpD2,
              feat4View.visible && styles.fadeUpVisible,
            )}
          >
            <GlobalSouthVisual visible={feat4View.visible} />
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  HOW IT WORKS                                                */}
      {/* ============================================================ */}
      <section className={styles.processSection} ref={processView.ref}>
        <div className={styles.processInner}>
          <div
            className={cx(
              styles.processHeader,
              styles.fadeUp,
              processView.visible && styles.fadeUpVisible,
            )}
          >
            <span className={styles.processEyebrow}>{t('process.eyebrow')}</span>
            <h2 className={styles.processTitle}>{t('process.title')}</h2>
          </div>

          <div className={styles.processSteps}>
            {[1, 2, 3, 4].map(n => (
              <div
                key={n}
                className={cx(
                  styles.processStep,
                  styles.fadeUp,
                  n === 2 && styles.fadeUpD1,
                  n === 3 && styles.fadeUpD2,
                  n === 4 && styles.fadeUpD3,
                  processView.visible && styles.fadeUpVisible,
                )}
              >
                <div
                  className={cx(
                    styles.stepNumber,
                    processView.visible && styles.stepNumberActive,
                  )}
                  style={{ transitionDelay: processView.visible ? `${0.15 * n}s` : '0s' }}
                >
                  {n}
                </div>
                <div className={styles.stepTitle}>
                  {t(`process.step${n}Title`)}
                </div>
                <div className={styles.stepDesc}>
                  {t(`process.step${n}Desc`)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  SERVICES CAROUSEL                                           */}
      {/* ============================================================ */}
      <section className={styles.servicesSection} ref={servicesView.ref}>
        <div className={styles.servicesInner}>
          <div
            className={cx(
              styles.servicesHeader,
              styles.fadeUp,
              servicesView.visible && styles.fadeUpVisible,
            )}
          >
            <span className={styles.servicesEyebrow}>{t('services.eyebrow')}</span>
            <h2 className={styles.servicesTitle}>{t('services.title')}</h2>
            <p className={styles.servicesSubtitle}>{t('services.subtitle')}</p>
          </div>

          <div className={styles.carouselWrap}>
            <button className={cx(styles.carouselArrow, styles.carouselArrowLeft)} onClick={() => scrollCarousel('left')} aria-label="Scroll left">
              <ChevronLeft size={20} />
            </button>
            <button className={cx(styles.carouselArrow, styles.carouselArrowRight)} onClick={() => scrollCarousel('right')} aria-label="Scroll right">
              <ChevronRight size={20} />
            </button>
            <div className={styles.carousel} ref={carouselRef}>
              {SERVICES.map((service, i) => {
                const Icon = service.icon
                return (
                  <Link
                    key={service.slug}
                    to={lp(`/services/${service.slug}`)}
                    className={cx(
                      styles.serviceCard,
                      styles.fadeUp,
                      i >= 1 && styles.fadeUpD1,
                      i >= 2 && styles.fadeUpD2,
                      i >= 3 && styles.fadeUpD3,
                      servicesView.visible && styles.fadeUpVisible,
                    )}
                  >
                    <div className={styles.serviceIconWrap}>
                      <Icon size={22} />
                    </div>
                    <h3 className={styles.serviceCardTitle}>{service.title}</h3>
                    <p className={styles.serviceCardDesc}>{service.description}</p>
                    <span className={styles.serviceCardLink}>
                      Learn more <ArrowRight size={14} />
                    </span>
                  </Link>
                )
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  CTA                                                         */}
      {/* ============================================================ */}
      <section className={styles.ctaSection} ref={ctaView.ref}>
        <div className={styles.ctaInner}>
          <h2
            className={cx(
              styles.ctaTitle,
              styles.fadeUp,
              ctaView.visible && styles.fadeUpVisible,
            )}
          >
            {t('cta.title')}
          </h2>
          <p
            className={cx(
              styles.ctaDesc,
              styles.fadeUp,
              styles.fadeUpD1,
              ctaView.visible && styles.fadeUpVisible,
            )}
          >
            {t('cta.subtitle')}
          </p>
          <Link
            to={lp('/contact')}
            className={cx(
              styles.ctaBtn,
              styles.fadeUp,
              styles.fadeUpD2,
              ctaView.visible && styles.fadeUpVisible,
            )}
          >
            {t('cta.button')}
          </Link>
        </div>
      </section>
    </>
  )
}
